import React, { useState, useEffect } from 'react';
import {
  keys,
  omit,
  zipObject,
  stubTrue,
  xor,
  cond,
  constant,
  get,
} from 'lodash';

import { Button, Checkbox, MiniLoader, Icon } from '@pwc/pwc-deals-ui';

import './MultiSelectSearchWithLabels.scss';

const applyButtonId = 'apply-linked-adjustment-button';
const createButtonId = 'create-linked-adjustment-button';
const optId = (_strings, opt) => {
  const id = `opt-${opt.text}`;
  return id.trim().replace(/\s/g, '');
};

const useBlurHandler = (ids, onFocusOutside) => (ev) => {
  const { currentTarget, relatedTarget } = ev;
  const focusedId = get(currentTarget, 'id', '');
  const focusIsWithin = ids.some((id) => focusedId === id);
  if (!relatedTarget && focusIsWithin) {
    onFocusOutside();
  } else if (currentTarget && !focusIsWithin && onFocusOutside) {
    onFocusOutside();
  }
};

const shouldShowApplyButton = ({
  searchCriteria,
  exactMatch,
  tagsChanged,
  allowCreate,
}) => {
  if (!allowCreate) {
    return tagsChanged;
  }

  return tagsChanged || (searchCriteria && exactMatch);
};

const getApplyButton =
  (
    onSubmit,
    currentlySelectedTags,
    blurHandler = () => {},
    handleEscapeKey = () => {},
    applyButtonText,
    applyButtonIcon
  ) =>
  ({ tagsChanged }) =>
    (
      <div className="multi-select-action-button-container">
        <Button
          id={applyButtonId}
          data-testid={applyButtonId}
          onClick={() => onSubmit(keys(currentlySelectedTags))}
          onBlur={blurHandler}
          type="button"
          disabled={!tagsChanged}
          onKeyDown={handleEscapeKey}
          className="button-xs"
        >
          <Icon
            name={applyButtonIcon}
            className="icon fill"
            height={18}
            width={18}
          />
          {applyButtonText}
        </Button>
      </div>
    );

const shouldShowCreateButton = ({
  searchCriteria,
  exactMatch,
  tagsChanged,
  allowCreate,
}) => {
  if (!allowCreate) {
    return false;
  }

  return searchCriteria && !exactMatch && !tagsChanged;
};

const getCreateButton =
  (
    onSubmit,
    currentlySelectedItems,
    blurHandler = () => {},
    handleEscapeKey = () => {}
  ) =>
  ({ searchCriteria }) =>
    (
      <div className="multi-select-action-button-container">
        <Button
          id={createButtonId}
          data-testid={createButtonId}
          onClick={() =>
            onSubmit([...keys(currentlySelectedItems), searchCriteria])
          }
          onBlur={blurHandler}
          type="button"
          onKeyDown={handleEscapeKey}
        >
          Create
        </Button>
      </div>
    );

const insensitive = (str) => {
  if (str) {
    return str.trim().toLowerCase();
  }
  return str;
};

const MultiSelectSearchWithLabels = ({
  searchCriteria,
  options,
  onSubmit,
  selectedLinkedAdjustments,
  allowCreate,
  closeDropdown,
  innerIdWhitelist = [],
  isLoading,
  applyButtonText = 'Apply',
  applyButtonIcon,
}) => {
  const [originalSelectedItems, setOrignalSelectedItems] = useState([]);
  const [currentlySelectedItems, setCurrentlySelectedItems] = useState({});
  const [viewableOptions, setViewableOptions] = useState([]);
  const [tagsChanged, setTagsChanged] = useState(false);
  const [exactMatch, setExactMatchChanged] = useState(false);

  useEffect(() => {
    const optionsList = Array.prototype.concat.apply(
      [],
      options.map((op) => op.items)
    );

    const optionsMap = optionsList.reduce(
      (acc, option) => ({ ...acc, [option.value]: option }),
      {}
    );

    setOrignalSelectedItems(
      selectedLinkedAdjustments.filter((tag) => optionsMap[tag])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentlySelectedItems(
      zipObject(originalSelectedItems, originalSelectedItems.map(stubTrue))
    );
  }, [originalSelectedItems]);

  useEffect(() => {
    setTagsChanged(
      xor(keys(currentlySelectedItems), originalSelectedItems).length
    );
  }, [currentlySelectedItems, originalSelectedItems]);

  useEffect(() => {
    const searchCriteriaInsensitive = insensitive(searchCriteria);
    const insensitiveOptionsText = options.map((option) =>
      insensitive(option.text)
    );
    const insensitiveSelectedTags = selectedLinkedAdjustments.map(insensitive);

    const filteredOptions = options.map((option) => {
      return {
        ...option,
        items: option.items.filter((item) => {
          const optionText = insensitive(item.text);
          return optionText.includes(searchCriteriaInsensitive);
        }),
      };
    });

    setViewableOptions(filteredOptions);

    setExactMatchChanged(
      !![...insensitiveOptionsText, ...insensitiveSelectedTags].find(
        (str) => str === searchCriteriaInsensitive
      )
    );
  }, [searchCriteria, options, selectedLinkedAdjustments]);

  const updateCheckedItems = (selectedItem) => {
    const alreadyChecked = currentlySelectedItems[selectedItem];
    if (alreadyChecked) {
      const updatedItems = omit(currentlySelectedItems, [selectedItem]);

      setCurrentlySelectedItems(updatedItems);
    } else {
      setCurrentlySelectedItems({
        ...currentlySelectedItems,
        [selectedItem]: true,
      });
    }
  };
  const allItems = options.reduce((acc, opt) => {
    acc = [...acc, ...opt.items];
    return acc;
  }, []);
  const blurHandler = useBlurHandler(
    [
      ...allItems.map((opt) => optId`${opt}`),
      ...innerIdWhitelist,
      applyButtonId,
      createButtonId,
    ],
    closeDropdown
  );

  const handleKeyDown = (ev, item) => {
    if (ev.keyCode === 27) {
      closeDropdown();
    }
    if (item && ev.keyCode === 13) {
      updateCheckedItems(item.text);
    }
  };

  const actions = cond([
    [
      shouldShowApplyButton,
      getApplyButton(
        onSubmit,
        currentlySelectedItems,
        blurHandler,
        handleKeyDown,
        applyButtonText,
        applyButtonIcon
      ),
    ],
    [
      shouldShowCreateButton,
      getCreateButton(
        onSubmit,
        currentlySelectedItems,
        blurHandler,
        handleKeyDown
      ),
    ],
    [stubTrue, constant(null)],
  ])({ tagsChanged, searchCriteria, exactMatch, allowCreate });

  if (isLoading === true) {
    return (
      <div
        className="multi-select-container loading"
        data-testid="multi-select-container"
      >
        <MiniLoader />
      </div>
    );
  }

  return (
    <div
      className="multi-select-container"
      data-testid="multi-select-container"
    >
      {viewableOptions.length > 0 && (
        <div className="scrollable">
          {viewableOptions.map((option) => (
            <div key={option.header}>
              <div className="items-header">{option.header}</div>
              {option.items.length === 0 && (
                <div className="items-not-found">Items not found</div>
              )}
              {option.items.map((item) => {
                return (
                  <Checkbox
                    key={`${option.header}_${item.text}`}
                    label={item.text}
                    id={optId`${item}`}
                    dataTestId={item.text}
                    checked={currentlySelectedItems[item.value] === true}
                    onChange={() => {
                      updateCheckedItems(item.value);
                    }}
                    onBlur={blurHandler}
                    onKeyDown={(ev) => {
                      handleKeyDown(ev, item);
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      )}
      {actions}
    </div>
  );
};

export default MultiSelectSearchWithLabels;
